import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useSnackbar } from "notistack";
import FormInput from "../../components/GeneralComponents/FormInput";
import { useAdminContext } from "../../Hooks/AdminContext";
import BasicBreadcrumbs from "../../components/GeneralComponents/BreadCrumbs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Editor from "../../components/editor/Editor";
import {
  Add_Product,
  Get_Product_By_Id,
  Update_Product,
} from "../../DAL/Listing/ProductList";
import CustomCircularProgress from "../../components/CustomCircularProgress";
import { picBaseUrl } from "../../config/config";

export const AddOrUpdateAds = ({ type }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setnavbarTitle } = useAdminContext();
  const { state } = useLocation();
  const [value, setValue] = useState("");
  const params = useParams();
  const [localInput, setLocalInput] = useState({
    title: "",
    amount: "",
    description: "",
    subscribe_button_text: "",
    manage_subscription_button_text: "",
    image: "",
    acount_type: "facebook",
    status: true,
  });

  const handleDescriptionChange = (value) => {
    setValue(value);
  };

  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (e, name) => {
    setLocalInput({ ...localInput, [name]: e.target.value });
  };

  /**
   * Handles form submission for adding or updating a product.
   * Constructs the payload from local state, determines whether to add or update based on `type`,
   * sends the API request, shows success/error notification, and navigates back to the product list on success.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = {
      title: localInput.title,
      amount: localInput.amount,
      status: localInput.status,
      product_type: localInput.acount_type,
      currency: "usd",
      image:
        localInput.acount_type === "facebook"
          ? "5d11380d-ec5e-4d3f-b5c6-de5975619ee2.svg"
          : "0d3db596-41a3-44d4-b46e-b72b1ad4105b.svg",
      short_description: localInput.description,
      subscribe_button_text: localInput.subscribe_button_text,
      subscription_button_text: localInput.manage_subscription_button_text,
      interval_type: "monthly",
      detailed_description: value,
    };
    const response =
      type === "add"
        ? await Add_Product(formData)
        : await Update_Product(formData, params.id);

    if (response.code === 200) {
      enqueueSnackbar(response.message, { variant: "success" });
      setLoading(false);
      if (!loading) {
        navigate("/product-list");
      }
    } else {
      setLoading(false);
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  // Breadcrumb navigation for Product module
  const BreadCrumbsList = [
    {
      title: "Product List",
      navigation: "/product-list",
      active: false,
    },
    {
      title: `${params.id ? "Edit" : "Add"} Product`,
      navigation: "",
      status: "Active",
    },
  ];

  // Fetch product details by ID and populate form state
  const GetProductDetails = async () => {
    setLoading(true);
    const response = await Get_Product_By_Id(params.id);
    if (response.code === 200) {
      // Populate form fields with API response
      setLocalInput({
        ...response.products,
        description: response.products.short_description,
        acount_type: response.products.product_type,
        manage_subscription_button_text:
          response.products.subscription_button_text,
      });

      // Set detailed description separately (e.g., for rich text editor)
      setValue(response.products.detailed_description);
      setLoading(false);
    } else {
      // Show error notification
      setLoading(false);
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  useEffect(() => {
    // Populate local state if navigated with product data (via location state)
    if (state) {
      setLocalInput({
        ...state,
        description: state.short_description,
        acount_type: state.product_type,
        manage_subscription_button_text: state.subscription_button_text,
      });
      setValue(state.detailed_description);
    }

    // If editing by URL param and state is not present, fetch product details
    else if (params.id && !state) {
      GetProductDetails();
    }

    // Set the top navigation title
    setnavbarTitle("Product List");
  }, [state]);

  if (loading) {
    return <CustomCircularProgress />;
  }

  return (
    <div className="container-fluid">
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-12 mb-3">
            <BasicBreadcrumbs items={BreadCrumbsList} />
          </div>
          <div className="col-md-6">
            <FormInput
              fullWidth
              label="Product Title"
              variant="outlined"
              value={localInput.title}
              onChange={(e) => handleChange(e, "title")}
              required
            />
          </div>
          <div className="col-md-6">
            <FormInput
              fullWidth
              label="Amount $"
              variant="outlined"
              value={localInput.amount}
              onChange={(e) => handleChange(e, "amount")}
              required
              type="number"
            />
          </div>
          <div className="col-md-6 mt-2">
            <FormControl fullWidth variant="outlined" required>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={localInput.acount_type}
                onChange={(e) => handleChange(e, "acount_type")}
                label="Account Type"
              >
                <MenuItem value="facebook">Facebook</MenuItem>
                <MenuItem value="tiktok">Tiktok</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="col-md-6 mt-2">
            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                value={localInput?.status}
                onChange={(e) => handleChange(e, "status")}
                label="Status"
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="col-md-6 mt-2">
            <FormInput
              fullWidth
              label="Subscribe Button Text"
              variant="outlined"
              value={localInput?.subscribe_button_text}
              onChange={(e) => handleChange(e, "subscribe_button_text")}
              required
            />
          </div>
          <div className="col-md-6 mt-2">
            <FormInput
              fullWidth
              label="Manage Subscription Button Text"
              variant="outlined"
              value={localInput?.manage_subscription_button_text}
              onChange={(e) =>
                handleChange(e, "manage_subscription_button_text")
              }
              required
            />
          </div>
          {/* <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
            <div className="row w-100 div-style ms-0 pt-0">
              <div className="col-lg-5 col-md-5 col-sm-12">
                <p className="">Upload Product Icon</p>
                <FormHelperText className="pt-0">
                  Image Size(500 X 500) ("JPG", "JPEG", "PNG","WEBP")
                </FormHelperText>
              </div>
              <div className="col-lg-2 col-md-2 col-6">
                {file ? (
                  <img className="image-border" src={file} height="50" />
                ) : (
                  <>
                    {localInput?.image && (
                      <img
                        className="image-border"
                        src={localInput?.image}
                        height="50"
                      />
                    )}
                  </>
                )}
              </div>
              <div className="col-lg-5 col-md-5 col-6 text-end pt-2">
                <label htmlFor="contained-button-file">
                  <Input
                    accept="image/*"
                    id="contained-button-file"
                    multiple
                    type="file"
                    name="image"
                    onChange={fileChangedHandler}
                  />

                  <Button
                    className="small-contained-button"
                    startIcon={
                      <FileUploadIcon
                        style={{
                          fill: "#fff",
                        }}
                      />
                    }
                    component="span"
                  >
                    Upload
                  </Button>
                </label>
              </div>
            </div>
            {localInput?.image?.name && (
              <p className="text-secondary mb-0">{localInput?.image?.name}</p>
            )}
          </div> */}
          <div className="col-12 mt-2">
            <FormInput
              label="Short Description"
              name="address"
              type="text"
              multline={true}
              maxLength={200} // Set max length for input
              value={localInput.description}
              onChange={(e) => {
                if (e.target.value.length <= 200) {
                  setLocalInput({ ...localInput, description: e.target.value });
                }
              }}
            />
            <small>{localInput.description.length}/200 characters</small>
          </div>
          <div className="col-12 mt-3">
            <h5
              style={{
                color: "#ACACAC",
              }}
            >
              Detail Description
            </h5>
            <Editor
              placeholder=""
              onChange={handleDescriptionChange}
              value={value}
            />
          </div>
          <div className="col-md-12 mt-3 text-end">
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
              className="small-contained-button"
            >
              {params.id ? "Update" : "Submit"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
